import type {
  IPassengerInfo,
  IRideDto,
  IRideMetadata,
  IStationInfo,
} from "../../dto";
import { addSeconds, calculateDateDifference } from "../date.helpers";

export const clearPassengersInfoForDuplicate = ({
  passengersInfo,
}: {
  passengersInfo: IPassengerInfo[] | undefined;
}): IPassengerInfo[] | undefined => {
  if (!passengersInfo || !Array.isArray(passengersInfo)) {
    return passengersInfo;
  }

  const clearedPassengersInfo = passengersInfo.map(
    passenger =>
      ({
        id: passenger.id,
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        code: passenger.code,
        phone: passenger.phone,
        phoneAlt: passenger.phoneAlt,
        sex: passenger.sex,
        unitId: passenger.unitId,
        unitName: passenger.unitName,
        siteId: passenger.siteId,
        from: passenger.from,
        to: passenger.to,
        seats: passenger.seats,
        passengerTypeId: passenger.passengerTypeId,
      }) satisfies IPassengerInfo,
  );

  return clearedPassengersInfo;
};

export const clearStationsInfoForDuplicate = ({
  stationsInfo,
}: {
  stationsInfo: IStationInfo[] | undefined;
}): IStationInfo[] | undefined => {
  if (!stationsInfo || !Array.isArray(stationsInfo)) {
    return stationsInfo;
  }

  const clearedStationsInfo: IStationInfo[] = stationsInfo.map(station => ({
    ...station,
    comment: undefined,
    realArrivalTime: undefined,
  }));

  return clearedStationsInfo;
};

/**
 * Re-bases every station's `arrivalTime` onto a new ride start, preserving the original offset of
 * each stop from the start of the ride.
 *
 * RYDE-7633: duplicating a past taxi ride for a new supplier moves rideStart/rideEnd to
 * now + lead time, but `metadata.stationsInfo[].arrivalTime` is nested and was left at the original
 * past timestamps — so the new ride carried per-stop times that precede its own start. Ride creation
 * does not recompute them for course rides or custom station routes, so the stale values survive.
 *
 * Returns the input untouched when either boundary is missing or the shift is a no-op.
 */
export const shiftStationsInfoArrivalTimes = ({
  stationsInfo,
  originalRideStart,
  newRideStart,
}: {
  stationsInfo: IStationInfo[] | undefined;
  originalRideStart: TDateISO | undefined;
  newRideStart: TDateISO | undefined;
}): IStationInfo[] | undefined => {
  if (!stationsInfo || !Array.isArray(stationsInfo)) {
    return stationsInfo;
  }

  if (!originalRideStart || !newRideStart) {
    return stationsInfo;
  }

  const offsetSeconds = calculateDateDifference(
    originalRideStart,
    newRideStart,
    "second",
    true,
  );

  if (!offsetSeconds) {
    return stationsInfo;
  }

  return stationsInfo.map(station =>
    station.arrivalTime
      ? {
          ...station,
          arrivalTime: addSeconds(station.arrivalTime, offsetSeconds),
        }
      : station,
  );
};

export const clearRideMetadataForDuplicate = ({
  metadata,
  reverseStations,
}: {
  metadata: IRideMetadata | undefined;
  reverseStations: boolean;
}): IRideMetadata => {
  if (!metadata) {
    return {};
  }

  const routeFields = (
    !reverseStations
      ? {
          customStationRouteId: metadata.customStationRouteId,
          usedCachedRouteId: metadata.usedCachedRouteId,
          excludedStationRouteIds: metadata.excludedStationRouteIds,
          routePolyline: metadata.routePolyline,
          directionsService: metadata.directionsService,
          isManualPolylineEdit: metadata.isManualPolylineEdit,
        }
      : {}
  ) satisfies Partial<IRideMetadata>;

  const clearedMetadata = {
    areaIds: metadata.areaIds,
    innerAreaIds: metadata.innerAreaIds,
    passengerIds: metadata.passengerIds,
    stationIds: metadata.stationIds,
    pickupStationsIds: metadata.pickupStationsIds,
    dropoffStationsIds: metadata.dropoffStationsIds,
    passengerTypeIds: metadata.passengerTypeIds,
    passengersCount: metadata.passengersCount,
    totalDuration: metadata.totalDuration,
    boardingSegmentDuration: metadata.boardingSegmentDuration,
    totalDistance: metadata.totalDistance,
    distances: metadata.distances,
    durations: metadata.durations,
    courseType: metadata.courseType,
    tags: metadata.tags,
    title: metadata.title,
    remarks: metadata.remarks,
    approver: metadata.approver,
    postReferentId: metadata.postReferentId,
    postReceiver: metadata.postReceiver,
    postReceiverPhone: metadata.postReceiverPhone,
    keepSupplier: metadata.keepSupplier,
    maxPassengers: metadata.maxPassengers,
    preserveStationsTiming: metadata.preserveStationsTiming,
    manualPrice: metadata.manualPrice,
    busQuantity: metadata.busQuantity,
    invoice: metadata.invoice,
    unitIds: metadata.unitIds,
    siteIds: metadata.siteIds,
    hasIndependentDayAdjustment: metadata.hasIndependentDayAdjustment,
    ...routeFields,
    passengersInfo: clearPassengersInfoForDuplicate({
      passengersInfo: metadata.passengersInfo,
    }),
    stationsInfo: clearStationsInfoForDuplicate({
      stationsInfo: metadata.stationsInfo,
    }),
  } satisfies IRideMetadata;

  return clearedMetadata;
};

export const clearRideForDuplicate = ({
  rideDto,
  reverseStations,
}: {
  rideDto: Partial<IRideDto>;
  reverseStations: boolean;
}): Partial<IRideDto> => {
  const clearedRideDto: Partial<IRideDto> = {
    companyId: rideDto.companyId,
    rideStart: rideDto.rideStart,
    rideEnd: rideDto.rideEnd,
    status: rideDto.status,
    direction: rideDto.direction,
    courseId: rideDto.courseId,
    courseName: rideDto.courseName,
    courseRouteType: rideDto.courseRouteType,
    courseSettingsSource: rideDto.courseSettingsSource,
    courseMergingMethod: rideDto.courseMergingMethod,
    courseRebuildRideOption: rideDto.courseRebuildRideOption,
    courseStationsIds: rideDto.courseStationsIds,
    rideTypeId: rideDto.rideTypeId,
    vehicleTypeId: rideDto.vehicleTypeId,
    rideTypeName: rideDto.rideTypeName,
    rideTypeCategory: rideDto.rideTypeCategory,
    reasonId: rideDto.reasonId,
    supplierId: rideDto.supplierId,
    siteId: rideDto.siteId,
    siteName: rideDto.siteName,
    journeyId: rideDto.journeyId,
    originStationId: rideDto.originStationId,
    originStationName: rideDto.originStationName,
    destinationStationId: rideDto.destinationStationId,
    destinationStationName: rideDto.destinationStationName,
    originAreaId: rideDto.originAreaId,
    destinationAreaId: rideDto.destinationAreaId,

    isRideWithEmptyStationsAllowed: rideDto.isRideWithEmptyStationsAllowed,
    isRideWithDuplicatedStationsAllowed:
      rideDto.isRideWithDuplicatedStationsAllowed,
    metadata: clearRideMetadataForDuplicate({
      metadata: rideDto.metadata,
      reverseStations,
    }),
  };

  return clearedRideDto;
};
